import React, { useState, useEffect, useRef } from 'react'
import { Box, Grid, Typography, IconButton, TextField } from '@mui/material'
import { EwayItem } from './Services'

const roundToTwo = (value: number): number =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100

const formatToTwo = (value: number): string => roundToTwo(value).toFixed(2)

export const totalValue = (item: EwayItem): number => {
  const { taxableAmount, igstRate, cgstRate, sgstRate } = item

  if (igstRate > 0) {
    return roundToTwo(
      Number(taxableAmount) + Number((taxableAmount * igstRate) / 100)
    )
  }

  // For CGST + SGST: Value = TVFV + (TVFV * CGST/100) + (TVFV * SGST/100)
  const cgstAmount = Number((taxableAmount * cgstRate) / 100)
  const sgstAmount = Number((taxableAmount * sgstRate) / 100)
  return roundToTwo(Number(taxableAmount) + cgstAmount + sgstAmount)
}

export const sumTaxableAmount = (items: EwayItem[]): number => {
  return roundToTwo(
    items.reduce((sum, item) => Number(sum) + Number(item.taxableAmount), 0)
  )
}

export const sumTotalValue = (items: EwayItem[]): number => {
  return roundToTwo(
    items.reduce((sum, item) => Number(sum) + Number(totalValue(item)), 0)
  )
}

export const sumCgstValue = (items: EwayItem[]): number => {
  return roundToTwo(
    items.reduce(
      (sum, item) =>
        Number(sum) + Number((item.taxableAmount * item.cgstRate) / 100),
      0
    )
  )
}

export const sumSgstValue = (items: EwayItem[]): number => {
  return roundToTwo(
    items.reduce(
      (sum, item) =>
        Number(sum) + Number((item.taxableAmount * item.sgstRate) / 100),
      0
    )
  )
}

export const sumIgstValue = (items: EwayItem[]): number => {
  return roundToTwo(
    items.reduce(
      (sum, item) =>
        Number(sum) + Number((item.taxableAmount * item.igstRate) / 100),
      0
    )
  )
}

export interface ItemListBoxProps {
  itemList: EwayItem[]
  setEwayItemList: (items: EwayItem[]) => void
}

const ItemListBox: React.FC<ItemListBoxProps> = ({
  itemList,
  setEwayItemList,
}) => {
  const [itemEntries, setItemEntries] = useState<EwayItem[]>([])
  const lastItemListRef = useRef(null)
  const handleInputChange = (index, event) => {
    const { name, value } = event.target
    const updatedItems = [...itemEntries]
    updatedItems[index] = { ...updatedItems[index], [name]: value }
    setItemEntries(updatedItems)
    setEwayItemList(updatedItems)
  }

  useEffect(() => {
    if (itemList && itemList.length > 0 && lastItemListRef.current !== itemList) {
      lastItemListRef.current = itemList
      setItemEntries(itemList)
      setEwayItemList(itemList)
    }
  }, [itemList, setEwayItemList])

  const isIGstValid = (item: EwayItem) => {
    if (item.igstRate && item.igstRate > 0) {
      return true
    }
    if (
      (item.cgstRate && item.cgstRate > 0) ||
      (item.sgstRate && item.sgstRate > 0)
    ) {
      return true
    }
    return false
  }

  const isCGstValid = (item: EwayItem) => {
    if (item.igstRate && item.igstRate > 0) {
      return true
    }
    if (item.cgstRate && item.cgstRate > 0) {
      return true
    }
    return false
  }

  const isSGstValid = (item: EwayItem) => {
    if (item.igstRate && item.igstRate > 0) {
      return true
    }
    if (item.sgstRate && item.sgstRate > 0) {
      return true
    }
    return false
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
        ITEM LIST
      </Typography>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={2}>
          <Typography variant="h6">Item Name</Typography>
        </Grid>
        <Grid item xs={1}>
          <Typography variant="h6">SAC</Typography>
        </Grid>
        <Grid item xs={1}>
          <Typography variant="h6">Quantity</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="h6">Taxable Value</Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Typography variant="h6">SGST(%)</Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Typography variant="h6">CGST(%)</Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Typography variant="h6">IGST(%)</Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Typography variant="h6">Value</Typography>
        </Grid>
      </Grid>
      {itemEntries.map((item, index) => (
        <Grid
          container
          spacing={2}
          key={index}
          alignItems="center"
          sx={{ mb: 1.5 }}
        >
          <Grid item xs={2}>
            <TextField
              fullWidth
              size="small"
              label="Item Name"
              name="itemName"
              value={item.productName}
              onChange={(event) => handleInputChange(index, event)}
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              size="small"
              fullWidth
              label="SAC"
              name="hsnCode"
              error={!item.hsnCode}
              value={item.hsnCode || ''}
              onChange={(event) => handleInputChange(index, event)}
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              fullWidth
              error={!item.quantity || item.quantity <= 0}
              size="small"
              label="Quantity"
              name="quantity"
              type="number"
              value={item.quantity}
              onChange={(event) => handleInputChange(index, event)}
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              error={!item.taxableAmount || item.taxableAmount <= 0}
              size="small"
              fullWidth
              label="Price"
              name="taxableAmount"
              type="number"
              value={item.taxableAmount}
              onChange={(event) => handleInputChange(index, event)}
            />
          </Grid>
          <Grid item xs={1.5}>
            <TextField
              size="small"
              fullWidth
              label="SGST(%)"
              name="sgstRate"
              value={item.sgstRate || ''}
              onChange={(event) => handleInputChange(index, event)}
            />
          </Grid>
          <Grid item xs={1.5}>
            <TextField
              size="small"
              fullWidth
              label="CGST(%)"
              name="cgstRate"
              value={item.cgstRate || ''}
              onChange={(event) => handleInputChange(index, event)}
            />
          </Grid>
          <Grid item xs={1.5}>
            <TextField
              fullWidth
              size="small"
              label="IGST(%)"
              name="igstRate"
              value={item.igstRate || ''}
              onChange={(event) => handleInputChange(index, event)}
            />
          </Grid>
          <Grid item xs={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Total"
              name="total"
              value={formatToTwo(totalValue(item))}
            />
          </Grid>
        </Grid>
      ))}
      <Grid container spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
        <Grid item xs={2}></Grid>
        <Grid item xs={1}></Grid>
        <Grid item xs={1}></Grid>
        <Grid item xs={2}>
          <TextField
            size="small"
            fullWidth
            label="Total Taxable Amt"
            name="taxableAmount"
            type="number"
            value={formatToTwo(sumTaxableAmount(itemEntries))}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            fullWidth
            size="small"
            label="Total SGST Value"
            name="total"
            value={formatToTwo(sumSgstValue(itemEntries))}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            fullWidth
            size="small"
            label="Total CGST Value"
            name="total"
            value={formatToTwo(sumCgstValue(itemEntries))}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            fullWidth
            size="small"
            label="Total IGST Value"
            name="total"
            value={formatToTwo(sumIgstValue(itemEntries))}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
        <Grid item xs={1.5}>
          <TextField
            fullWidth
            size="small"
            label="Total Invoice Value"
            name="total"
            value={formatToTwo(sumTotalValue(itemEntries))}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default ItemListBox
